<template>
  <v-form ref="form" v-model="formValid" validate-on="submit" @submit.prevent>
    <v-radio-group v-model="localValue.item.type" class="mb-4" inline>
      <v-radio
        class="mr-4"
        v-for="type in types"
        :key="type"
        :value="type"
        :ripple="false"
        :label="$t(`network.settings.trustedNetworkTypes.${type}`)"
      />
    </v-radio-group>
    <v-text-field
      v-if="localValue.item.type === TrustedNetworkType.SSID"
      maxlength="150"
      v-model="localValue.item.value"
      variant="outlined"
      :rules="[required(), valueRule]"
      :label="$t(`modals.${config.action}.ssidLabel`)"
      :aria-label="$t(`modals.${config.action}.ssidLabel`)"
      :placeholder="$t(`modals.${config.action}.ssidPlaceholder`)"
    />
    <v-text-field
      v-else
      v-model="localValue.item.value"
      variant="outlined"
      :rules="[required(), ipAddressRule, valueRule]"
      :label="$t(`modals.${config.action}.ipLabel`)"
      :aria-label="$t(`modals.${config.action}.ipLabel`)"
      :placeholder="$t(`modals.${config.action}.ipPlaceholder`)"
    />
  </v-form>
</template>

<script lang="ts">
import { defineComponent, ref, type PropType, watch, computed } from "vue";
import { required } from "@/_helpers/validators.ts";
import cloneDeep from "lodash/cloneDeep";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type { ErrorPayload } from "@/_store/errors.module.ts";
import { TrustedNetworkType } from "@/constants/network.ts";
import Patterns from "@/constants/patterns.ts";
import { useI18n } from "vue-i18n";
import type { VuetifyFormRef } from "@/types.ts";

const defaultConfig = {
  type: TrustedNetworkType.SSID,
  value: "",
};

export default defineComponent({
  emits: ["update:localValue", "update:valid", "update:errors"],
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
    errors: {
      type: Array as PropType<ErrorPayload[]>,
      default() {
        return [];
      },
    },
  },
  setup(props, { emit }) {
    const form = ref<VuetifyFormRef>();
    const { t } = useI18n();
    const localValue = ref(
      props.config.item
        ? { item: { ...props.config.item } }
        : { item: { ...cloneDeep(defaultConfig) } }
    );
    const formValid = ref(true);
    const previousValue = ref("");
    const valueErrorMessage = ref("");

    const valid = computed(() => {
      return props.errors.length === 0;
    });

    const valueError = computed(() => {
      return props.errors.find((e: ErrorPayload) => e.field === "value");
    });

    const valueRule = (name: string = "") => {
      if (valueError.value) {
        previousValue.value = name;
        valueErrorMessage.value = valueError.value.message;
        emit(
          "update:errors",
          props.errors.filter((e) => e.field !== "value")
        );
        return valueErrorMessage.value;
      }
      if (previousValue.value !== name) {
        previousValue.value = name;
        valueErrorMessage.value = "";
      }
      return true;
    };

    const ipAddressRule = (value: string) => {
      if (!value) return t("validations.required");
      if (!Patterns.IP_WITHOUT_CIDR.test(value)) {
        return t("validations.ipAddress");
      }
      return true;
    };

    const validate = async () => {
      const { valid } = (await form.value?.validate()) ?? { valid: false };
      return valid;
    };

    watch(
      () => localValue.value,
      () => {
        emit("update:localValue", {
          ...localValue.value,
        });
      },
      { deep: true }
    );

    watch(formValid, () => {
      if (props.errors.length === 0) {
        // Treat null as valid (not yet validated), only false means invalid
        emit("update:valid", formValid.value !== false);
      }
    });

    watch(
      () => localValue.value.item.type,
      () => {
        localValue.value.item.value = "";
      }
    );

    watch(
      () => localValue.value.item.value,
      () => {
        if (form.value?.resetValidation) {
          form.value.resetValidation();
        }
      }
    );

    watch(
      localValue,
      () => {
        emit("update:localValue", localValue.value);
        if (props.errors.length && !valid.value && !valueError.value) {
          emit("update:errors");
        }
      },
      { deep: true }
    );

    return {
      localValue,
      formValid,
      types: [TrustedNetworkType.SSID, TrustedNetworkType.IP],
      required,
      TrustedNetworkType,
      ipAddressRule,
      form,
      valueRule,
      validate,
    };
  },
});
</script>

<style scoped lang="scss"></style>
